import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Stack, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { v4 as uuidv4 } from 'uuid';

import { PhoneBase, InputUI, ButtonUI, SwitchUI } from '../Interface';
import itemService from '../../service/item.service';
import { useLoader } from '../../provider/LoaderProvider';
import { DocumentType } from '../../config/DocumentType';
import dayjs from 'dayjs';
import useConfig from '../../context/ConfigContext';
import useParam from '../../context/ParamContext';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import serviceCoreService from '../../service/serviceCore.service';
import { cleanValues } from '../../utils/helper';
import ItemsDialog from './ItemsDialog';
import SearchCompanyDialog from './SearchCompanyDialog';
import vatCodeService from '../../service/vatCode.service';
import userService from '../../service/user.service';
import PinCodeLogInDialog from '../Interface/PinCodeLogInDialog';

const DeliveryDraft = () => {
  const timeoutRef = useRef(null);
  const itemInputRef = useRef(null);
  const { config, getConfig } = useConfig();
  const { getParam, params } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const [openSearch, setOpenSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  // add states for items and quantity handling
  const [itemFilter, setItemFilter] = useState('');
  const [docLines, setDocLines] = useState([]);
  const [fullSearch, setFullSearch] = useState(false);
  const [items, setItems] = useState([]);

  const defLogInData = {
    pinCode: '',
    userId: null,
  };
  const [logInData, setLogInData] = useState(defLogInData);
  const [showLogInDialog, setShowLogInDialog] = useState(true);

  useEffect(() => {
    Object.keys(config).length === 0 && getConfig();
    Object.keys(params).length === 0 && getParam();
  }, []);

  useEffect(() => {
    setDocLines([]);
  }, [selectedCompany]);

  useEffect(() => {
    if (logInData.pinCode) {
      getUserByPinCode();
    } else {
      setShowLogInDialog(true);
    }
  }, [logInData.pinCode]);

  useEffect(() => {
    if (!logInData.pinCode || !params.SERVICE_ACTIVITY) {
      return;
    }

    const handleActivity = () => resetTimer();

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('keydown', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('touchstart', handleActivity);

    resetTimer();

    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      clearTimeout(timeoutRef.current);
    };
  }, [logInData.pinCode, params]);

  const defaultDocumentLine = {
    lineStatus: 'O',
    itemCode: null,
    itemName: null,
    itemCodeInBp: null,
    qty: null,
    cplQty: null,
    opnQty: null,
    quantityUnitId: null,
    quantityUnitCode: null,
    quantityUnitName: null,
    whsCode: null,
    toWhsCode: null,
    unitPrice: null,
    currencyCode: null,
    vatCode: null,
    vatPercent: null,
    vatValue: null,
    netPrice: null,
    grossPrice: null,
    dueDate: dayjs(),
    vatValueInOwnCur: null,
    netPriceInOwnCur: null,
    grossPriceInOwnCur: null,
    weightChangeNumber: null,
    weight: null,
    projectId: null,
    costCenterCode: null,
    itemReference: null,
    internalNote: '',
    bpNote: '',
    isBillable: false,
    baseId: null,
    baseLine: null,
    baseDocnum: null,
    baseDocType: null,
    warranty: false,
    docLineBatchSn: [],
  };

  const defaultValues = {
    docTypeId: DocumentType.Delivery,
    docStatus: 'P',
    stornoId: null,
    docDate: dayjs(),
    issueDate: dayjs(),
    dueDate: dayjs(),
    companyId: null,
    companyCode: '',
    companyName: '',
    companyContactId: null,
    shortName: '',
    incotermCity: '',
    companyWarning: '',
    currencyCode: null,
    exchangeRate: null,
    paymentMethodId: null,
    reference: '',
    projectId: null,
    costCenterCode: null,
    salesOrPurchasePersonId: null,
    billingAdressId: null,
    billingCountry: '',
    billingProvince: '',
    billingPostCode: '',
    billingCity: '',
    billingStreetAddress: '',
    shippingAdressId: null,
    shippingCountry: '',
    shippingProvince: '',
    shippingPostCode: '',
    shippingCity: '',
    shippingStreetAddress: '',
    deliveryMethodId: null,
    incotermCode: null,
    whsCode: config?.defaultWarehouse || null,
    toWhsCode: null,
    priceListId: null,
    langCode: null,
    hunTaxNumber: null,
    euTaxNumber: null,
    foreignTaxNumber: null,
    sumInDocCur: null,
    netInDocCur: null,
    VATInDocCur: null,
    roundingInDocCur: null,
    sumInOwnCur: null,
    netInOwnCur: null,
    VATInOwnCur: null,
    roundingInOwnCur: null,
    note: '',
    internalNote: '',
    docLine: [],
  };

  const handleQtyChange = (id) => (e) => {
    const val = Math.max(0, Number(e.target.value));
    setDocLines((prev) => prev.map((l) => (l.id === id ? { ...l, qty: val } : l)));
  };

  const resetTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setLogInData(defLogInData);

      resetTimer();
    }, params.SERVICE_ACTIVITY);
  };

  const getUserByPinCode = () => {
    userService.getUserByPinCode(logInData.pinCode).then((data) => {
      if (Object.keys(data).length > 0) {
        setLogInData((prev) => ({
          ...prev,
          userId: data.userId,
        }));
        setShowLogInDialog(false);
        setOpenSearch(true);
      } else {
        addNotification({
          content: 'Nem található felhasználó ezzel a PIN kóddal!',
          type: NotificationType.ERROR,
        });
      }
    });
  };

  const handleOpenSearch = () => {
    setOpenSearch(true);
    setSearchQuery('');
  };

  const handleCloseSearch = () => {
    setOpenSearch(false);
    setTimeout(() => {
      if (itemInputRef.current) itemInputRef.current.focus();
    }, 0);
  };

  const addItem = async (data) => {
    if (!selectedCompany) {
      addNotification({
        content: 'Előbb válasszon ki egy ügyfelet!',
        type: NotificationType.WARNING,
      });
      return;
    }

    const resolvedVatCode = await vatCodeService.getVatCodeToDoc({
      documentTypeId: DocumentType.Delivery,
      itemCode: data.itemCode,
      companyId: selectedCompany.id ?? null,
    });

    let selectedVat = null;
    if (resolvedVatCode) {
      selectedVat = await vatCodeService.getVatCode(resolvedVatCode);
    }

    const newLine = {
      ...defaultDocumentLine,
      id: uuidv4(),
      itemCode: data.itemCode,
      itemName: data.itemName,
      qty: 1,
      whsCode: config.defaultWarehouse || null,
      quantityUnitId: data.quantityUnit?.id,
      quantityUnitCode: data.quantityUnit?.code,
      quantityUnitName: data.quantityUnit?.name,
      vatCode: resolvedVatCode || null,
      vatPercent: selectedVat ? selectedVat.percentage : null,

      // isBillable already false by defaultDocumentLine; override here if needed later
    };
    setDocLines((prev) => [...prev, newLine]);
    setItemFilter('');
  };

  const searchItem = () => {
    const val = itemFilter.trim();
    if (!val) return;
    showLoader();
    itemService
      .findItem({ query: val, fullSearch })
      .then((data) => {
        if (data.length > 1) {
          setItems(data);
          return;
        }
        if (!data || !data?.[0]?.itemCode) {
          addNotification({
            content: `Nincs találat a megadott cikkre. (${val})`,
            type: NotificationType.WARNING,
          });

          setTimeout(() => {
            setItemFilter('');
            if (itemInputRef.current) itemInputRef.current.focus();
          }, 500);

          return;
        }

        addItem(data[0]);
      })
      .finally(hideLoader);
  };

  const handleRemoveLine = (id) => {
    setDocLines((prev) => prev.filter((l) => l.id !== id));
  };

  // validate and submit
  const handleRegister = () => {
    const errors = [];
    if (!selectedCompany) errors.push('Nincs kiválasztott partner.');
    if (docLines.length === 0) errors.push('Nincs hozzáadott cikk.');
    if (docLines.some((l) => Number(l.qty) <= 0)) errors.push('Minden mennyiségnek 0-nál nagyobbnak kell lennie.');

    if (errors.length) {
      addNotification({
        content: errors.join(' /n'),
        type: NotificationType.ERROR,
      });
      return;
    }

    const doc = {
      ...cleanValues(defaultValues),
      companyId: selectedCompany.id,
      companyCode: selectedCompany.code,
      companyName: selectedCompany.name,
      currencyCode: selectedCompany.customerCurrencyCode || config.currencyCode,
      docLine: docLines.map((l) => cleanValues(l)),
      createdBy: logInData.userId,
    };

    serviceCoreService.createDocument(doc).then(() => {
      addNotification({
        content: 'A szállítólevél tervezet sikeresen rögzítve lett.',
        type: NotificationType.SUCCESS,
      });
      setSelectedCompany(null);
      setDocLines([]);
      setSearchQuery('');
    });
  };

  const isValid = !!selectedCompany && docLines.length > 0 && docLines.every((l) => Number(l.qty) > 0);

  return (
    <PhoneBase name="DeliveryDraft" sx={{ p: 2 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        Szállítólevél rögzítése
      </Typography>
      <Box>
        <Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              minHeight: 80,
              mt: 1.5,
              p: 1.5,
              bgcolor: 'grey.100',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h7" component="h4">
                Ügyfél
              </Typography>
              <IconButton size="small" onClick={handleOpenSearch} aria-label="Keresés">
                <SearchIcon fontSize="small" />
              </IconButton>
            </Box>

            {selectedCompany && (
              <Box
                key={selectedCompany.id}
                sx={{
                  px: 1.5,
                  py: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  cursor: 'pointer',
                  bgcolor: 'white',
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                  {selectedCompany.code} - {selectedCompany.name}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            minHeight: 80,
            mt: 1.5,
            p: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Typography variant="h7" component="h4">
            Cikkek
          </Typography>

          {/* item filter input */}
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1, bgcolor: 'white' }}>
              <InputUI
                autoFocus
                fullWidth
                size="small"
                placeholder={fullSearch ? 'Cikknév/cikkszám' : 'Cikkszám/vonalkód'}
                value={itemFilter}
                onChange={(e) => setItemFilter(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    searchItem();
                  }
                }}
                inputRef={itemInputRef}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" edge="end" aria-label="Keresés" onClick={searchItem}>
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <SwitchUI checked={fullSearch} onChange={(e) => setFullSearch(e.target.checked)} />
          </Box>

          {/* rögzített cikkek */}
          <Stack spacing={0.25} sx={{ mt: 1.5 }}>
            {docLines.map((item) => {
              const key = item.id;
              return (
                <Box
                  key={key}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'white',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                        {item.itemCode} - {item.itemName}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InputUI
                        name="qty"
                        type="number"
                        size="small"
                        sx={{ width: 110 }}
                        inputProps={{ min: 0, step: 1 }}
                        value={item.qty ?? 0}
                        onChange={handleQtyChange(key)}
                      />
                      <IconButton color="error" size="small" aria-label="Törlés" onClick={() => handleRemoveLine(key)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 0.5,
                      mt: 0.5,
                    }}
                  ></Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
        <Box textAlign="center" mt="2rem">
          <ButtonUI text="Rögzítés" className="bg-success" onClick={handleRegister} disabled={!isValid} />
        </Box>
      </Box>

      <PinCodeLogInDialog
        open={showLogInDialog}
        handleClose={() => setShowLogInDialog(false)}
        logInData={logInData}
        setLogInData={setLogInData}
      />

      <SearchCompanyDialog
        open={openSearch}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        handleClose={handleCloseSearch}
        setSelectedCompany={setSelectedCompany}
      />

      <ItemsDialog
        open={items.length > 0}
        handleClose={() => {
          setItems([]);
          setItemFilter('');
          if (itemInputRef.current) itemInputRef.current.focus();
        }}
        items={items}
        addItem={addItem}
      />
    </PhoneBase>
  );
};

export default DeliveryDraft;
